/* TypeScript DTOs for Documents API */

import { DocumentCategory, DocumentPermission, DocumentScope, DocumentType } from '../../../../enums/DocumentPermission';

// Re-export enums for convenience
export { DocumentCategory, DocumentPermission, DocumentScope, DocumentType };

/* ---- Core client infrastructure ---- */
export interface ClientConfig {
  baseUrl?: string;
  fetchFn?: typeof fetch;
  defaultHeaders?: Record<string, string>;
}

export class ApiError extends Error {
  constructor(public status: number, public responseBody: string) {
    super(`API Error ${status}: ${responseBody}`);
  }
}

/* ---- DTOs ---- */

/** Folder permission assignment */
export interface FolderPermissionDto {
  id: number;
  folderId: number;
  roleId?: number | null;
  userId?: number | null;
  franchiseId?: number | null;
  storeId?: number | null;
  permissions: DocumentPermission[];
  createdAt: string;
  createdByUserId: number;
}

/** Folder/directory in the document tree */
export interface DocumentFolderDto {
  id: number;
  name: string;
  description?: string | null;
  parentFolderId?: number | null;
  scope: DocumentScope;
  category: DocumentCategory;
  franchiseId?: number | null;
  storeId?: number | null;
  taskTemplateId?: number | null;
  taskInstanceId?: number | null;
  evidenceDate?: string | null;
  path: string[];
  childFolderCount: number;
  documentCount: number;
  effectivePermissions: DocumentPermission[];
  createdAt: string;
  createdByUserId: number;
  updatedAt?: string | null;
}

/** A single version of a document file */
export interface DocumentVersionDto {
  id: number;
  documentId: number;
  versionNumber: number;
  fileName: string;
  fileSize: number;
  mimeType: string;
  documentType: DocumentType;
  blobUrl: string;
  thumbnailUrl?: string | null;
  changeNotes?: string | null;
  createdAt: string;
  createdByUserId: number;
  createdByUserName?: string | null;
}

/** Document metadata - wrapper containing one or more versions */
export interface DocumentDto {
  id: number;
  folderId: number;
  name: string;
  description?: string | null;
  tags: string[];
  linkedTaskTemplateIds?: number[];
  versionCount: number;
  latestVersion: DocumentVersionDto;
  versions?: DocumentVersionDto[];
  createdAt: string;
  createdByUserId: number;
  createdByUserName?: string | null;
  updatedAt?: string | null;
  updatedByUserId?: number | null;
}

/** Request to create a new folder */
export interface CreateFolderRequest {
  name: string;
  description?: string | null;
  parentFolderId?: number | null;
  scope: DocumentScope;
  category: DocumentCategory;
  franchiseId?: number | null;
  storeId?: number | null;
  taskTemplateId?: number | null;
}

/** Request to update folder metadata */
export interface UpdateFolderRequest {
  name: string;
  description?: string | null;
  category: DocumentCategory;
}

/** Request to upload a document */
export interface UploadDocumentRequest {
  folderId: number;
  name: string;
  description?: string | null;
  tags?: string[];
  linkedTaskTemplateIds?: number[];
}

/** Request to update document metadata */
export interface UpdateDocumentRequest {
  name: string;
  description?: string | null;
  tags?: string[];
  linkedTaskTemplateIds?: number[];
}

/** Request to upload a new version of an existing document */
export interface UploadVersionRequest {
  documentId: number;
  changeNotes?: string | null;
}

/** Request to add/update folder permissions */
export interface SetFolderPermissionRequest {
  roleId?: number | null;
  userId?: number | null;
  franchiseId?: number | null;
  storeId?: number | null;
  permissions: DocumentPermission[];
}

/** Request to store task evidence */
export interface StoreTaskEvidenceRequest {
  taskInstanceId: number;
  taskTemplateId: number;
  storeId: number;
  evidenceDate: string;
  evidenceType: string;
  description?: string | null;
}

/** Folder tree node for hierarchical display */
export interface FolderTreeNodeDto {
  folder: DocumentFolderDto;
  children: FolderTreeNodeDto[];
}

/** Search/filter parameters for documents */
export interface DocumentSearchParams {
  query?: string;
  folderId?: number;
  scope?: DocumentScope;
  category?: DocumentCategory;
  franchiseId?: number;
  storeId?: number;
  documentType?: DocumentType;
  tags?: string[];
  taskTemplateId?: number;
  fromDate?: string;
  toDate?: string;
  page?: number;
  pageSize?: number;
}

/** Paginated search result */
export interface DocumentSearchResult {
  documents: DocumentDto[];
  totalCount: number;
  page: number;
  pageSize: number;
}

/* ---- Helper functions ---- */

/** Format file size for display */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/** Get icon name for document type */
export function getDocumentTypeIcon(documentType: DocumentType): string {
  switch (documentType) {
    case DocumentType.PDF: return 'PictureAsPdf';
    case DocumentType.Image: return 'Image';
    case DocumentType.Video: return 'VideoFile';
    case DocumentType.Document: return 'Description';
    case DocumentType.Spreadsheet: return 'TableChart';
    case DocumentType.Presentation: return 'Slideshow';
    case DocumentType.Archive: return 'FolderZip';
    default: return 'InsertDriveFile';
  }
}

/** Get document type from MIME type */
export function getDocumentTypeFromMime(mimeType: string): DocumentType {
  if (mimeType === 'application/pdf') return DocumentType.PDF;
  if (mimeType.startsWith('image/')) return DocumentType.Image;
  if (mimeType.startsWith('video/')) return DocumentType.Video;
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) return DocumentType.Spreadsheet;
  if (mimeType.includes('presentation') || mimeType.includes('powerpoint')) return DocumentType.Presentation;
  if (mimeType.includes('document') || mimeType.includes('word')) return DocumentType.Document;
  if (mimeType.includes('zip') || mimeType.includes('archive')) return DocumentType.Archive;
  return DocumentType.Other;
}
